<template>
  <transition name="slide-x-reverse-transition">
    <v-card v-if="modelValue && task" class="subtask-panel elevation-8">
      <v-card-actions class="pane-header">
        <v-spacer></v-spacer>
        <span class="text-body-1 text-grey">Sub-tasks</span>
        <v-spacer></v-spacer>
        <v-btn icon size="small" @click="$emit('update:modelValue', false)">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-actions>

      <v-card-title class="task-header">
        <div class="text-body-2 text-grey">{{ task.code }}</div>
        <div class="text-h6 task-title">{{ task.title }}</div>
      </v-card-title>

      <v-list class="subtask-list" :lines="false">
        <v-list-item
          v-for="subtask in subtasks"
          :key="subtask.id"
          class="subtask-tile"
        >
          <v-list-item-subtitle
            v-if="subtask.itemTemplateCode"
            :title="subtask.itemTemplateCode"
            class="text-grey"
          >
            {{ subtask.itemTemplateCode }}
          </v-list-item-subtitle>
          <v-list-item-title :title="subtask.title">
            {{ subtask.title }}
          </v-list-item-title>
          <v-list-item-subtitle v-if="leadByTeamName(subtask)" class="text-grey">
            {{ leadByTeamName(subtask) }}
          </v-list-item-subtitle>
          <template #append>
            <v-chip
              v-if="displayProgress(subtask).length"
              class="progress-container text-white"
              label
              color="grey"
              variant="flat"
            >
              <div
                :style="{ width: displayProgress(subtask) }"
                class="progress-bar"
              ></div>
              <div class="progress-text">{{ displayProgress(subtask) }}</div>
            </v-chip>
          </template>
        </v-list-item>
        <v-list-item v-if="!subtasks.length">
          <v-list-item-title class="text-grey">No sub-tasks</v-list-item-title>
        </v-list-item>
      </v-list>
    </v-card>
  </transition>
</template>

<script setup>
import { computed } from 'vue';

const props = defineProps({
  modelValue: {
    type: Boolean,
    default: false
  },
  // Task object carrying a `subtasks` array
  task: {
    type: Object,
    default: null
  },
  teams: {
    type: Array,
    default: () => []
  }
});

defineEmits(['update:modelValue']);

const subtasks = computed(() => props.task?.subtasks || []);

const leadByTeamName = (subtask) => {
  return props.teams.find(team => team.id === subtask.leadByTeamId)?.name || '';
};

// API progress is a 0-1 fraction; tolerate pre-scaled 0-100 values.
const displayProgress = (subtask) => {
  const progress = subtask.progress;
  if (typeof progress !== 'number') return '';
  const percent = progress <= 1 ? progress * 100 : progress;
  return `${parseFloat(percent.toFixed(2))}%`;
};
</script>

<style scoped>
.subtask-panel {
  position: absolute;
  top: 24px;
  right: 24px;
  width: 408px;
  max-width: calc(100% - 48px);
  max-height: calc(100% - 48px);
  display: flex;
  flex-direction: column;
  z-index: 10;
}

.pane-header {
  padding: 4px 8px;
  min-height: 0;
}

.task-header {
  padding: 0 16px 8px;
}

.task-title {
  white-space: normal;
  line-height: 1.3;
}

.subtask-list {
  overflow-y: auto;
  flex: 1 1 auto;
  padding-top: 0;
}

.subtask-tile {
  min-height: 56px;
}

.progress-container {
  width: 70px;
  flex-shrink: 0;
  justify-content: center;
  padding: 0 !important;
  margin: 4px;
}

.progress-container :deep(.v-chip__content) {
  white-space: normal;
}

.progress-bar {
  position: absolute;
  left: 0;
  height: 100%;
  background-color: #aed581; /* green lighten-2 */
  border-radius: 1px;
  transition: width 0.5s ease;
}

.progress-text {
  line-height: 1.2;
  text-align: center;
  min-width: 40px;
  white-space: nowrap;
  z-index: 1;
}
</style>
